'use client'

import { useState } from 'react'
import { getGeocode } from 'use-places-autocomplete'

import type { AddressDetails } from '@/shared/ui/address/AddressDetailsForm'

import { parseGeocodeResult } from '../lib/geocode'
import { buildManualDescription, buildSavedAddressFromManual, buildSavedAddressFromSelection } from '../lib/savedAddress'
import type { AddressMode, AtHomeAddressResult, ManualAddress, PickerMode, ProfileAddress, SavedAddress } from './types'

const EMPTY_MANUAL: ManualAddress = { street: '', city: '', postalCode: '', country: '' }

type UseAtHomeAddressParams = {
  savedAddresses: SavedAddress[]
  profileAddress?: ProfileAddress | null
  isLoggedIn: boolean
  onSelect: (result: AtHomeAddressResult) => void
  onSaveAddress?: (address: Omit<SavedAddress, '_id'>) => void
}

/**
 * State machine for the at-home address picker: which pane is shown (saved list
 * vs. new entry), the pending selection and its access details, and confirming /
 * optionally persisting it. Pure parsing/payload work lives in `../lib`.
 */
export function useAtHomeAddress({ savedAddresses, profileAddress, isLoggedIn, onSelect, onSaveAddress }: UseAtHomeAddressParams) {
  const hasSavedAddresses = isLoggedIn && savedAddresses.length > 0

  const [mode, setMode] = useState<PickerMode>(hasSavedAddresses ? 'list' : 'new')
  const [addressMode, setAddressMode] = useState<AddressMode>('search')
  const [manualAddress, setManualAddress] = useState<ManualAddress>(EMPTY_MANUAL)
  const [selectedAddress, setSelectedAddress] = useState<AtHomeAddressResult | null>(null)
  const [details, setDetails] = useState<AddressDetails>({})
  const [showDetails, setShowDetails] = useState(false)
  const [saveAddress, setSaveAddress] = useState(false)
  const [newAddressLabel, setNewAddressLabel] = useState('')

  const canSaveAddress = isLoggedIn && !!onSaveAddress

  const hasProfileAddress = Boolean(isLoggedIn && profileAddress?.street && profileAddress?.city)
  const profileAlreadySaved = hasProfileAddress && savedAddresses.some((a) => a.street === profileAddress?.street && a.city === profileAddress?.city)

  const resetPendingAddress = () => {
    setSelectedAddress(null)
    setDetails({})
    setShowDetails(false)
    setSaveAddress(false)
    setNewAddressLabel('')
    setManualAddress(EMPTY_MANUAL)
  }

  // Switching between address search and manual entry starts from a clean slate so a pending
  // selection (and its access-details/confirm) from the other mode doesn't linger.
  const switchAddressMode = (next: AddressMode) => {
    if (next === addressMode) return
    setAddressMode(next)
    resetPendingAddress()
  }

  const goToNew = () => setMode('new')

  const goToList = () => {
    setMode('list')
    resetPendingAddress()
  }

  const selectSavedAddress = (address: SavedAddress) => {
    setSelectedAddress({
      description: `${address.street}, ${address.postalCode} ${address.city}`,
      placeId: address.placeId || '',
      apartment: address.apartment,
      floor: address.floor,
      doorCode: address.doorCode,
      instructions: address.instructions,
      postalCode: address.postalCode,
      coordinates: address.coordinates,
    })
    setDetails({
      apartment: address.apartment || undefined,
      floor: address.floor || undefined,
      doorCode: address.doorCode || undefined,
      instructions: address.instructions || undefined,
    })
    setShowDetails(true)
  }

  const selectProfileAddress = () => {
    if (!profileAddress?.street || !profileAddress?.city) return
    setSelectedAddress({
      description: `${profileAddress.street}, ${profileAddress.cp || ''} ${profileAddress.city}`,
      placeId: '',
      postalCode: profileAddress.cp != null ? String(profileAddress.cp) : undefined,
    })
    setDetails({})
    setShowDetails(true)
    setMode('new')
    setAddressMode('search')
  }

  const selectSearchResult = async (place: google.maps.places.AutocompletePrediction) => {
    const base: AtHomeAddressResult = { description: place.description, placeId: place.place_id }
    setDetails({})
    setShowDetails(true)
    setSelectedAddress(base)

    try {
      const [result] = await getGeocode({ placeId: place.place_id })
      setSelectedAddress({ ...base, ...parseGeocodeResult(result, place) })
    } catch (e) {
      // Keep the raw prediction; confirm falls back to parsing the description.
    }
  }

  const saveIfRequested = (address: Omit<SavedAddress, '_id'>) => {
    if (saveAddress && onSaveAddress && newAddressLabel && isLoggedIn) {
      onSaveAddress(address)
    }
  }

  const confirm = () => {
    if (!selectedAddress) return
    onSelect({ ...selectedAddress, ...details })
    saveIfRequested(buildSavedAddressFromSelection({ selectedAddress, details, label: newAddressLabel, savedCount: savedAddresses.length }))
  }

  const confirmManual = () => {
    if (!manualAddress.street || !manualAddress.city) return
    onSelect({
      description: buildManualDescription(manualAddress),
      placeId: '',
      postalCode: manualAddress.postalCode || undefined,
      ...details,
    })
    saveIfRequested(buildSavedAddressFromManual({ manual: manualAddress, details, label: newAddressLabel, savedCount: savedAddresses.length }))
  }

  return {
    // derived flags
    hasSavedAddresses,
    hasProfileAddress,
    profileAlreadySaved,
    canSaveAddress,
    // state
    mode,
    addressMode,
    manualAddress,
    selectedAddress,
    details,
    showDetails,
    saveAddress,
    newAddressLabel,
    // setters
    setManualAddress,
    setDetails,
    setSaveAddress,
    setNewAddressLabel,
    toggleDetails: () => setShowDetails((v) => !v),
    // actions
    goToNew,
    goToList,
    switchAddressMode,
    selectSavedAddress,
    selectProfileAddress,
    selectSearchResult,
    confirm,
    confirmManual,
  }
}
